import { Typography } from '@material-ui/core';
import {
  AccountBalanceWallet,
  Business,
  ExitToApp,
  Help,
  Info,
  Person,
  Settings,
} from '@material-ui/icons';
import { useDeepEqualValue } from '@superdispatch/hooks';
import { Column, Columns, useResponsiveValue } from '@superdispatch/ui';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { trackEvent } from 'shared/helpers/Analytics';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { IntercomChatIcon } from 'shared/icons/IntercomChatIcon';
import { OutlinedWeakPasswordIcon } from 'shared/icons/OutlinedWeakPasswordIcon';
import { useSubscriptionPaymentSeverity } from 'shared/layout/notification-stack/data/NotificationAPI';
import { CarrierMenuItemsLegacy } from 'shared/layout/sidebar/AppCarrierMenuLegacy';
import { useDispatcherProfile } from 'shared/modules/dispatcher/DispatcherAPI';
import { useExperimentState } from 'shared/modules/experiments/ExperimentsAPI';
import { useOnboardingDialogState } from 'shared/modules/onboarding/core/OnboardingWrapper';
import { useShowAddBillingAddressDialog } from 'shared/modules/subscription/core/useShowAddBillingAddressDialog';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import {
  SidebarLinkLegacy,
  SidebarMenuLinkLegacy,
  StyledLinkLegacyIcon,
} from './AppSidebarMenuLegacy';
import { useDispatcherProfileVisitedValue } from './IsDispatcherProfileVisited';
import { showDefaultBotpressFab } from 'shared/modules/botpress/Botpress';
import { TextBoxIcon } from 'shared/icons';
import {
  useHasOnboardingPackets,
  useHasPendingOnboardingPackets,
} from 'shared/modules/onboarding-packets/data/OnboardingPacketsAPI';

function ChatWithSupportMenuItem() {
  const { showLauncherMessanger } = useCustomerSupport();
  const handleClick = () => {
    showDefaultBotpressFab();
    showLauncherMessanger();
  };

  return (
    <SidebarMenuLinkLegacy onClick={handleClick}>
      <Columns align="center" space="small">
        <Column width="content">
          <StyledLinkLegacyIcon as={IntercomChatIcon} />
        </Column>

        <Column width="fluid">
          <Typography variant="subtitle1">Chat with Support</Typography>
        </Column>
      </Columns>
    </SidebarMenuLinkLegacy>
  );
}

interface CarrierMenuLegacyProps {
  localPaths?: {
    dashboard?: string;
    loads?: string;
    contacts?: string;
    users?: string;
    drivers?: string;
    trips?: string;
    tracking?: string;
    loadboard?: string;
    ratings?: string;
    dispatcherProfile?: string;
    carrierProfile?: string;
    reports?: string;
    settings?: string;
    subscriptionDetails?: string;
    logout?: string;
    dispatcherManagement?: string;
    onboardingPackets?: string;
  };
}

const helpCenterUrl = 'https://support.superdispatch.com';

export function useCarrierMenuLegacy({
  localPaths: localPathsProp = {},
}: CarrierMenuLegacyProps) {
  const location = useLocation();
  const isWebview = MobileAppBridge.isInjected();
  const { data: dispatcher } = useDispatcherProfile();
  const { openOnboardingDialog } = useOnboardingDialogState();
  const localPaths = useDeepEqualValue(localPathsProp);
  const isVisitedDispatcherProfile = useDispatcherProfileVisitedValue();
  const { data: settings } = useCarrierSettings();
  const isSuperPayBankError = !!settings?.superpay.payment_error;
  const { isInExperiment } = useExperimentState('Onboarding', {
    options: { enabled: !isWebview },
  });
  const { doesBillingAddressNotExist } = useShowAddBillingAddressDialog();
  const isMobile = useResponsiveValue(true, false);
  const { hasOnboardingPackets } = useHasOnboardingPackets();
  const hasPendingOnboardingPackets = useHasPendingOnboardingPackets();
  const subscriptionSeverity = useSubscriptionPaymentSeverity();

  const isShowWeakPassword =
    !isVisitedDispatcherProfile &&
    location.pathname !== '/users' &&
    !dispatcher?.p_changed_at;

  const accountItemsSection = useMemo((): SidebarLinkLegacy[] => {
    if (isInExperiment) {
      return [
        {
          key: 'chat-with-support',
          label: '',
          href: '',
          hidden: !isMobile,
          RenderComponent: ChatWithSupportMenuItem,
        },
        {
          key: 'getting-started',
          Icon: Help,
          label: 'Getting Started',
          href: '#',
          dataIntercomTarget: 'onboarding-menu-item',
          onClick: () => {
            openOnboardingDialog();
          },
        },
        {
          key: 'help-center',
          Icon: Info,
          label: 'Help Center',
          href: helpCenterUrl,
          target: '_blank',
          onClick: () => {
            trackEvent('CTMS: Clicked Help Center');
          },
        },
        {
          key: 'logout',
          Icon: ExitToApp,
          label: 'Log Out',
          href: '/tms/logout',
          localPath: localPaths.logout,
        },
      ];
    }

    return [
      {
        key: 'chat-with-support',
        label: '',
        href: '',
        hidden: !isMobile,
        RenderComponent: ChatWithSupportMenuItem,
      },
      {
        key: 'help-center',
        Icon: Info,
        label: 'Help Center',
        href: helpCenterUrl,
        target: '_blank',
        onClick: () => {
          trackEvent('CTMS: Clicked Help Center');
        },
      },
      {
        key: 'logout',
        Icon: ExitToApp,
        label: 'Log Out',
        href: '/tms/logout',
        localPath: localPaths.logout,
      },
    ];
  }, [isInExperiment, isMobile, localPaths.logout, openOnboardingDialog]);

  const { ratingsLink, carrierItems, dispatcherItems, accountItems } =
    useMemo<CarrierMenuItemsLegacy>(
      () => ({
        ratingsLink: {
          key: 'view-ratings',
          label: 'View Ratings',
          href: '/tms/profile/overview',
          localPath: localPaths.ratings,
        },
        carrierItems: [
          {
            key: 'settings',
            Icon: Settings,
            label: 'Settings',
            href: '/tms/settings',
            localPath: localPaths.settings,
          },
          {
            key: 'carrier-profile',
            Icon: Business,
            label: 'Carrier Profile',
            href: '/tms/profile',
            localPath: localPaths.carrierProfile,
            hasError: isSuperPayBankError,
          },
          ...(hasOnboardingPackets
            ? [
                {
                  key: 'onboarding-packets',
                  Icon: TextBoxIcon,
                  label: 'Onboarding Packets',
                  href: '/tms/onboarding-packets',
                  localPath: localPaths.onboardingPackets,
                  badgeType: 'dot' as const,
                  badgeContent: hasPendingOnboardingPackets ? 1 : undefined,
                },
              ]
            : []),
          {
            key: 'subscription-details',
            Icon: AccountBalanceWallet,
            label: 'Subscription Details',
            href: '/tms/settings/subscription',
            localPath: localPaths.subscriptionDetails,
            hasError: doesBillingAddressNotExist,
            severity: subscriptionSeverity,
          },
        ],
        dispatcherItems: [
          {
            key: 'dispatcher-profile',
            Icon: Person,
            label: 'My Profile',
            href: '/tms/users',
            localPath: localPaths.users,
            RightIcon: isShowWeakPassword
              ? OutlinedWeakPasswordIcon
              : undefined,
          },
        ],
        accountItems: accountItemsSection,
      }),
      [
        localPaths.ratings,
        localPaths.settings,
        localPaths.carrierProfile,
        localPaths.subscriptionDetails,
        localPaths.users,
        localPaths.onboardingPackets,
        isSuperPayBankError,
        isShowWeakPassword,
        doesBillingAddressNotExist,
        subscriptionSeverity,
        accountItemsSection,
        hasOnboardingPackets,
        hasPendingOnboardingPackets,
      ],
    );

  return { ratingsLink, carrierItems, dispatcherItems, accountItems };
}
